import React, { useMemo, useRef, useState, Suspense, useEffect } from 'react';
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls } from 'three-stdlib';

extend({ OrbitControls });

const DEFAULT_PANEL = { w: 1.13, h: 2.28, depth: 0.04 };

function polygonToMeters(polygonLatLng) {
  if (!polygonLatLng || polygonLatLng.length < 3) return null;
  const lats = polygonLatLng.map(p => p.lat);
  const lngs = polygonLatLng.map(p => p.lng);
  const latC = (Math.min(...lats) + Math.max(...lats)) / 2;
  const lngC = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos((latC * Math.PI) / 180);
  const pts = polygonLatLng.map(p => [
    (p.lng - lngC) * mPerDegLng,
    -(p.lat - latC) * mPerDegLat,
  ]);
  const xs = pts.map(p => p[0]);
  const zs = pts.map(p => p[1]);
  return {
    points: pts,
    width: Math.max(...xs) - Math.min(...xs),
    depth: Math.max(...zs) - Math.min(...zs),
  };
}

function Rooftop({ shape, height = 4 }) {
  const geometry = useMemo(() => {
    const s = new THREE.Shape();
    shape.points.forEach((p, i) => {
      if (i === 0) s.moveTo(p[0], p[1]);
      else s.lineTo(p[0], p[1]);
    });
    s.closePath();
    const geo = new THREE.ExtrudeGeometry(s, { depth: height, bevelEnabled: false });
    geo.rotateX(-Math.PI / 2);
    return geo;
  }, [shape, height]);

  return (
    <group>
      <mesh geometry={geometry} castShadow receiveShadow>
        <meshStandardMaterial color="#e7e5e4" roughness={0.9} />
      </mesh>
      <mesh position={[0, height + 0.05, 0]} castShadow receiveShadow>
        <boxGeometry args={[shape.width + 0.5, 0.15, shape.depth + 0.5]} />
        <meshStandardMaterial color="#475569" roughness={0.85} />
      </mesh>
    </group>
  );
}

function PanelArray({ rows, cols, panelW, panelH, tilt, azimuth, gap = 0.05, baseY = 4.3 }) {
  const totalW = cols * panelW + (cols - 1) * gap;
  const totalD = rows * panelH + (rows - 1) * gap;
  const tiltRad = (tilt * Math.PI) / 180;
  const azRad = ((azimuth - 180) * Math.PI) / 180;
  const items = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = -totalW / 2 + c * (panelW + gap) + panelW / 2;
      const z = -totalD / 2 + r * (panelH + gap) + panelH / 2;
      items.push(
        <group key={`${r}-${c}`} position={[x, baseY, z]} rotation={[tiltRad, 0, 0]}>
          <mesh castShadow>
            <boxGeometry args={[panelW, 0.03, panelH]} />
            <meshStandardMaterial
              color="#1d4ed8"
              metalness={0.4}
              roughness={0.25}
              emissive="#0c1e64"
              emissiveIntensity={0.05}
            />
          </mesh>
          <mesh position={[0, -0.02, 0]}>
            <boxGeometry args={[panelW + 0.02, 0.02, panelH + 0.02]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.5} />
          </mesh>
        </group>
      );
    }
  }
  return <group rotation={[0, azRad, 0]}>{items}</group>;
}

function Sun({ azimuth = 180, elevation = 60 }) {
  const az = (azimuth * Math.PI) / 180;
  const el = (elevation * Math.PI) / 180;
  const distance = 80;
  const x = distance * Math.sin(az) * Math.cos(el);
  const y = distance * Math.sin(el);
  const z = -distance * Math.cos(az) * Math.cos(el);
  return (
    <group position={[x, y, z]}>
      <mesh>
        <sphereGeometry args={[3, 24, 24]} />
        <meshBasicMaterial color="#fbbf24" />
      </mesh>
      <pointLight color="#fff7d6" intensity={500} distance={400} />
    </group>
  );
}

function Ground({ size = 80 }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow position={[0, -0.01, 0]}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial color="#86efac" roughness={1} />
    </mesh>
  );
}

function Controls({ target, minDistance, maxDistance }) {
  const { camera, gl } = useThree();
  const controlsRef = useRef();
  useEffect(() => {
    const controls = new OrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = minDistance;
    controls.maxDistance = maxDistance;
    controls.maxPolarAngle = Math.PI / 2.1;
    controls.target.set(target[0], target[1], target[2]);
    controls.update();
    controlsRef.current = controls;
    return () => controls.dispose();
  }, [camera, gl.domElement, minDistance, maxDistance, target]);
  useFrame(() => { controlsRef.current?.update(); });
  return null;
}

function AutoRotator({ enabled, children }) {
  const ref = useRef();
  useFrame((_, dt) => { if (enabled && ref.current) ref.current.rotation.y += dt * 0.15; });
  return <group ref={ref}>{children}</group>;
}

export default function RooftopViewer3D({
  polygon, rows = 4, cols = 6, tilt = 15, azimuth = 180,
  panelW = DEFAULT_PANEL.w, panelH = DEFAULT_PANEL.h,
  sunElevation = 55, sunAzimuth = 160,
  height = '480px', autoRotate = false, className = '',
}) {
  const shape = useMemo(() => {
    if (polygon && polygon.length >= 3) return polygonToMeters(polygon);
    const w = Math.max(10, cols * panelW + 4);
    const d = Math.max(10, rows * panelH + 4);
    return {
      points: [[-w/2, -d/2], [w/2, -d/2], [w/2, d/2], [-w/2, d/2]],
      width: w, depth: d,
    };
  }, [polygon, rows, cols, panelW, panelH]);

  const cameraDist = Math.max(20, Math.max(shape.width, shape.depth) * 1.4);
  const [rotate, setRotate] = useState(autoRotate);

  return (
    <div className={`relative ${className}`} style={{ height }}>
      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [cameraDist * 0.8, cameraDist * 0.7, cameraDist * 0.9], fov: 40 }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
        style={{ background: 'linear-gradient(to bottom, #87ceeb 0%, #cfe9ff 60%, #d8f5e0 100%)' }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.6} />
          <hemisphereLight color="#a0d8ef" groundColor="#86efac" intensity={0.4} />
          <directionalLight
            position={[40, 60, 30]}
            intensity={2}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-camera-far={150}
            shadow-camera-left={-50}
            shadow-camera-right={50}
            shadow-camera-top={50}
            shadow-camera-bottom={-50}
          />
          <Ground size={Math.max(60, Math.max(shape.width, shape.depth) * 2.5)} />
          <Sun azimuth={sunAzimuth} elevation={sunElevation} />
          <AutoRotator enabled={rotate}>
            <Rooftop shape={shape} height={4} />
            <PanelArray
              rows={rows} cols={cols}
              panelW={panelW} panelH={panelH}
              tilt={tilt} azimuth={azimuth}
              baseY={4.35}
            />
          </AutoRotator>
          <Controls
            target={[0, 4, 0]}
            minDistance={cameraDist * 0.4}
            maxDistance={cameraDist * 3}
          />
        </Suspense>
      </Canvas>
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-semibold text-emerald-900 shadow border border-emerald-100">
        {rows * cols} panels · drag to rotate · scroll to zoom
      </div>
      <button
        onClick={() => setRotate(v => !v)}
        className="absolute bottom-3 right-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-semibold text-emerald-900 shadow border border-emerald-100 hover:bg-amber-100 transition-colors"
      >
        {rotate ? '⏸ Stop spin' : '▶ Auto-spin'}
      </button>
    </div>
  );
}
